import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Tag {
  id: string;
  name: string;
  description?: string;
  color: string;
  type: string;
}

export const useTagAssignment = (
  resourceType: "device" | "profile" | "application" | "configuration" | "patch_policy" | "user"
) => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const getJunctionTable = useCallback(() => {
    switch (resourceType) {
      case "device": return "device_tags";
      case "profile": return "profile_tags";
      case "application": return "application_tags";
      case "configuration": return "configuration_tags";
      case "patch_policy": return "patch_policy_tags";
      case "user": return "device_user_tags";
    }
  }, [resourceType]);

  const getResourceColumn = useCallback(() => {
    switch (resourceType) {
      case "device": return "device_id";
      case "profile": return "profile_id";
      case "application": return "application_id";
      case "configuration": return "configuration_id";
      case "patch_policy": return "patch_policy_id";
      case "user": return "device_user_id";
    }
  }, [resourceType]);

  const assignTag = useCallback(async (resourceId: string, tagId: string) => {
    setIsLoading(true);
    try {
      const junctionTable = getJunctionTable();
      const resourceColumn = getResourceColumn();
      
      const { error } = await supabase
        .from(junctionTable as any)
        .insert({
          [resourceColumn as string]: resourceId,
          tag_id: tagId
        });

      if (error) throw error;

      toast({
        title: "Tag assigned",
        description: "Tag has been successfully assigned.",
      });

      return true;
    } catch (error) {
      console.error("Error assigning tag:", error);
      toast({
        title: "Error",
        description: "Failed to assign tag. Please try again.",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [getJunctionTable, getResourceColumn, toast]);

  const removeTag = useCallback(async (resourceId: string, tagId: string) => {
    setIsLoading(true);
    try {
      const junctionTable = getJunctionTable();
      const resourceColumn = getResourceColumn();
      
      const { error } = await supabase
        .from(junctionTable as any)
        .delete()
        .eq(resourceColumn as string, resourceId)
        .eq("tag_id", tagId);

      if (error) throw error;

      toast({
        title: "Tag removed",
        description: "Tag has been successfully removed.",
      });

      return true;
    } catch (error) {
      console.error("Error removing tag:", error);
      toast({
        title: "Error",
        description: "Failed to remove tag. Please try again.",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [getJunctionTable, getResourceColumn, toast]);

  const getAssignedTags = useCallback(async (resourceId: string): Promise<Tag[]> => {
    try {
      const junctionTable = getJunctionTable();
      const resourceColumn = getResourceColumn();
      
      const { data, error } = await supabase
        .from(junctionTable as any)
        .select(`
          tags (
            id,
            name,
            description,
            color,
            type
          )
        `)
        .eq(resourceColumn as string, resourceId);

      if (error) throw error;

      return (data as any)?.map((item: any) => item.tags).filter(Boolean) || [];
    } catch (error) {
      console.error("Error fetching assigned tags:", error);
      return [];
    }
  }, [getJunctionTable, getResourceColumn]);

  const bulkAssignTags = useCallback(async (resourceId: string, tagIds: string[]) => {
    setIsLoading(true);
    try {
      const junctionTable = getJunctionTable();
      const resourceColumn = getResourceColumn();
      
      const insertData = tagIds.map(tagId => ({
        [resourceColumn as string]: resourceId,
        tag_id: tagId
      }));

      const { error } = await supabase
        .from(junctionTable as any)
        .insert(insertData);

      if (error) throw error;

      toast({
        title: "Tags assigned",
        description: `${tagIds.length} tag(s) have been successfully assigned.`,
      });

      return true;
    } catch (error) {
      console.error("Error bulk assigning tags:", error);
      toast({
        title: "Error",
        description: "Failed to assign tags. Please try again.",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [getJunctionTable, getResourceColumn, toast]);

  return {
    assignTag,
    removeTag,
    getAssignedTags,
    bulkAssignTags,
    isLoading
  };
};